<script lang="ts" setup>
	import type { uiClass } from './index.vue'
	import type { ListContext } from '@/components/Shared/list-item.vue'

	defineProps<{
		show: boolean
		header: string
		summary: ListContext
		btnText?: string
		loading?: boolean
		needToKnow?: string[] | string
		action?: 'deactivate' | 'activate' | '' | 'remove'
		ui?: Partial<uiClass>
	}>()

	const emits = defineEmits<{
		(arg: 'update:show', param: boolean): void
		(arg: 'cancel', param: boolean): void
		(
			arg: 'proceed',
			param: {
				password: string
				terms: boolean
			}
		): void
	}>()

	const isTermsOpen = shallowRef(false)
	const form = shallowReactive({
		password: '',
		terms_accepted: false
	})
	const proceed = () => {
		if (form.password.length > 4) {
			emits('proceed', {
				...form,
				terms: form.terms_accepted
			})
		} else {
			useNotification().alert({
				status: 400,
				message: 'Password length must be greater than 4'
			})
		}
	}
	const cancel = () => {
		emits('update:show', false)
		emits('cancel', false)
		form.password = ''
		form.terms_accepted = false
	}
</script>

<template>
	<div>
		<dialogs-error
			:use-cancel-btn="false"
			:show="show"
			:icon-props="{
				dir: 'fill',
				icon: 'warning',
				wrapperClass: 'border'
			}"
			:header="header"
			:ui
			wrapper-class="bg-gray-50 py-9 px-5 w-full"
			modal-button-text="Continue"
			@close="cancel"
		>
			<template #paragraph>
				<div class="w-full text-left">
					<template v-if="Array.isArray(needToKnow)">
						<h4 class="mb-2 text-base font-medium leading-tight text-slate-700">Need to know:</h4>

						<ul class="mt-3">
							<li v-for="(item, index) in needToKnow" :key="index" class="mt-2 flex items-start gap-2">
								<div class="m-2 h-[7px] w-[7px] flex-none rounded-full bg-purple-400 text-purple-500" />
								<div class="pb- font-normal text-gray-600" v-html="item" />
							</li>
						</ul>
					</template>
					<div v-else class="mt-8 text-center" v-html="needToKnow" />
				</div>
			</template>

			<template #button>
				<shared-list-item
					class="mb-4 w-full rounded-md border border-purple-200 bg-purple-50"
					:lists="summary"
				/>
				<a-form :model="form" class="my-3 w-full" @finish="proceed">
					<a-form-item v-if="!form.terms_accepted" required class="!mb-7">
						<div class="flex w-full items-center justify-center gap-3">
							<a-checkbox v-model:checked="form.terms_accepted" />

							<div class="text-gray-600">
								I have read and I accept the
								<span
									aria-describedby="button"
									aria-colindex="0"
									class="cursor-pointer font-medium text-primary-2"
									@click="() => (isTermsOpen = true)"
								>
									Terms & Conditions
								</span>
							</div>
						</div>
					</a-form-item>

					<a-form-item v-if="form.terms_accepted" label="Password" required class="!mb-7 !mt-4">
						<a-input-password
							v-model:value="form.password"
							autocomplete="new password"
							class="custom-input w-full !px-5"
							name="confirm_password"
						/>
					</a-form-item>

					<div class="flex items-center justify-between gap-x-8">
						<a-button
							class="default-btn w-full justify-center !bg-gray-200 font-semibold !text-gray-700 hover:!bg-gray-200"
							@click="cancel"
						>
							Back
						</a-button>

						<a-button
							:loading="loading"
							html-type="submit"
							:disabled="form.password.length < 4"
							class="default-btn w-full justify-center"
							:class="{
								'bg-primary-2': action === 'activate',
								'!bg-error-500 !text-white hover:!bg-error-600 disabled:!bg-error-300':
									action === 'deactivate' || action === 'remove'
							}"
						>
							<span v-if="action === 'activate'">
								{{ !form.terms_accepted ? 'Proceed' : btnText || 'Add' }}
							</span>
							<span v-else class="capitalize">
								{{ !form.terms_accepted ? 'Continue' : action }}
							</span>
						</a-button>
					</div>
				</a-form>
			</template>
		</dialogs-error>
		<dialogs-terms-and-condition v-model:show="isTermsOpen" />
	</div>
</template>
